import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/organisms/Layout";
import Input from "../../components/form/Input";
import TextArea from "../../components/form/TextArea";
import { toast } from "react-hot-toast";
import ReactImageUploading from "react-images-uploading";
import Select from "../../components/form/Select";
import BrandApi from "../../api/Brand";
import Company from "../../api/Company";
import { decodeIds } from "../../utils";

const EditBrand = () => {
  const { t } = useTranslation();
  const [edit, setEdit] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [urlSlug, setUrlSlug] = useState("");
  const [brand, setBrand] = useState(null);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [company, setCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [facebook, setFacebook] = useState("");
  const [instagram, setInstagram] = useState("");
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [images, setImages] = useState([]);
  const decodedId = decodeIds(params?.brandId)[0];
  const domainUrl =
    process.env.REACT_APP_API_BASE_URL ===
    "https://stagingapi.heyhire.net/api/v1/"
      ? "https://staging.heyhire.app"
      : "https://heyhire.app";

  useEffect(() => {
    document.title = "HeyHire Portal-Edit-Brand";
    loadCompanies();
  }, []);

  const loadBrand = () => {
    BrandApi.getById(decodedId)
      .then((response) => {
        const brandFromResponse = response.data.data;
        setBrand(brandFromResponse);
        setName(brandFromResponse.name);
        setWebsite(brandFromResponse.website);
        setCompany(brandFromResponse.company_id);
        setDescription(brandFromResponse.description);
        setFacebook(brandFromResponse.facebook);
        setInstagram(brandFromResponse.instagram);
        setUrlSlug(brandFromResponse.url_slug);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error("Something went wrong.");
        }
      })
      .finally(() => setLoadingCompany(false));
  };

  const loadCompanies = () => {
    setLoadingCompany(true);
    Company.getAll()
      .then((response) => {
        setCompanies(response.data.data);
      })
      .catch()
      .finally(() => loadBrand());
  };

  const updateBrand = () => {
    setUpdateLoading(true);
    setValidationErrors(null);

    let body = {
      id: decodedId,
      name: name,
      company_id: company,
      website: website,
      facebook: facebook,
      url_slug: urlSlug,
      description: description,
      instagram: instagram,
    };

    BrandApi.update(decodedId, body)
      .then(() => {
        toast.success("Changes Saved");
        if (images.length > 0) {
          uploadBrandPhoto();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error("Something Went Wrong. Please Try Again.");
          }
        }
      })
      .then(() => setUpdateLoading(false));
  };

  const uploadBrandPhoto = () => {
    setUpdateLoading(true);
    BrandApi.uploadImage(decodedId, images[0].file).finally(() =>
      setUpdateLoading(false)
    );
  };

  return (
    <DashboardLayout >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/companies/brands"
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="far fa-chevron-left text-xl" />
              </Link>
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Brand
              </h2>
            </div>
            <button
              onClick={() => setEdit(!edit)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-primary-600 shadow-sm ring-1 ring-inset ring-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={updateLoading}
            >
              {t("common.edit")}
              {edit ? (
                <i className="fad fa-lock-open" />
              ) : (
                <i className="fad fa-lock" />
              )}
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Update the information below to modify this brand.
          </p>
        </div>

        {loadingCompany ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <i className="fad fa-circle-notch fa-spin text-2xl text-primary-500" />
          </div>
        ) : (
          brand && (
            <div className="bg-white">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateBrand();
                }}
                className="space-y-8"
              >
                {/* Basic Information */}
                <div>
                  <h3 className="text-base font-semibold leading-7 text-gray-900">
                    Basic Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Primary details about the brand.
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                    <div className="col-span-full sm:col-span-1">
                      <Input
                        name="name"
                        label="Brand Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={
                          validationErrors && validationErrors.name
                            ? validationErrors.name[0]
                            : null
                        }
                        disabled={!edit}
                        
                      />
                    </div>

                    <div className="col-span-full sm:col-span-1">
                      <Select
                        label="Company"
                        value={company}
                        name="company"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || v === undefined || v === null) {
                            setCompany("");
                          } else {
                            const id = Number(v);
                            setCompany(Number.isNaN(id) ? "" : id);
                          }
                        }}
                        disabled={!edit}
                        
                      >
                        {companies.map((company) => (
                          <option
                            className="p-5"
                            key={company.id}
                            value={company.id}
                          >
                            {company.name}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div className="col-span-full sm:col-span-1">
                      <Input
                        name="url_slug"
                        label="Custom URL"
                        slug={domainUrl}
                        placeholder="business-name"
                        value={urlSlug}
                        onChange={(e) => setUrlSlug(e.target.value)}
                        error={
                          validationErrors && validationErrors.url_slug
                            ? validationErrors.url_slug[0]
                            : null
                        }
                        disabled={!edit}
                      />
                    </div>

                    <div className="col-span-full">
                      <TextArea
                        label="Brand Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        error={
                          validationErrors && validationErrors.description
                            ? validationErrors.description[0]
                            : null
                        }
                        disabled={!edit}
                        darmo={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Brand Assets */}
                <div>
                  <h3 className="text-base font-semibold leading-7 text-gray-900">
                    Brand Assets
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Upload your brand logo and cover image.
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                    <div className="col-span-full sm:col-span-1">
                      <ReactImageUploading
                        value={images}
                        onChange={(imageList) => {
                          setImages(imageList);
                        }}
                      >
                        {({ imageList, onImageUpload }) => (
                          <div className="flex items-center gap-4">
                            <div className="h-28 w-28 bg-gray-100">
                              {imageList && imageList.length > 0 ? (
                                <img
                                  className="h-full w-full object-cover"
                                  src={imageList[0]?.dataURL}
                                  alt=""
                                />
                              ) : brand.photo ? (
                                <img
                                  className="h-full w-full object-cover"
                                  src={brand?.photo?.thumb_url}
                                  alt=""
                                />
                              ) : (
                                <i
                                  className="text-gray-400 text-3xl fa fa-plus cursor-pointer"
                                  onClick={onImageUpload}
                                />
                              )}
                            </div>
                            <button
                              type="button"
                              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-primary-600 shadow-sm ring-1 ring-inset ring-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={onImageUpload}
                              disabled={!edit}
                            >
                              Upload Brand Logo
                            </button>
                          </div>
                        )}
                      </ReactImageUploading>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <h3 className="text-base font-semibold leading-7 text-gray-900">
                    Social Media
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Connect your brand's social media accounts.
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                    <div className="col-span-full sm:col-span-1">
                      <Input
                        name="website"
                        label="Website"
                        slug="http://"
                        placeholder="www.example.com"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        error={
                          validationErrors && validationErrors.website
                            ? validationErrors.website[0]
                            : null
                        }
                        disabled={!edit}
                        
                      />
                    </div>

                    <div className="col-span-full sm:col-span-1">
                      <Input
                        name="facebook"
                        label="Facebook Page"
                        slug="facebook.com/"
                        value={facebook}
                        onChange={(e) => setFacebook(e.target.value)}
                        error={
                          validationErrors && validationErrors.facebook
                            ? validationErrors.facebook[0]
                            : null
                        }
                        disabled={!edit}
                        
                      />
                    </div>

                    <div className="col-span-full sm:col-span-1">
                      <Input
                        name="instagram"
                        label="Instagram Page"
                        slug="instagram.com/"
                        value={instagram}
                        onChange={(e) => setInstagram(e.target.value)}
                        error={
                          validationErrors && validationErrors.instagram
                            ? validationErrors.instagram[0]
                            : null
                        }
                        disabled={!edit}
                        
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="border-t border-gray-900/10">
                  <button
                    type="button"
                    onClick={() => navigate("/companies/brands")}
                    className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateLoading}
                    className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                  >
                    {updateLoading ? (
                      <i className="fad fa-circle-notch fa-spin" />
                    ) : (
                      t("common.save")
                    )}
                  </button>
                </div>
              </form>
            </div>
          )
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditBrand;
